import { useCallback, useEffect, useState } from "react";
import { listGeofenceZones } from "./geofenceApi.js";

export const GEOFENCE_ZONES_DEFAULT_PAGE_SIZE = 25;

/**
 * Fetches GET /api/backoffice/geofence/zones with real server-side
 * pagination/filtering - mirrors ../abnormal/useAbnormalEventsList.js.
 * Refetches every time `filters` changes (page, pageSize, search, zoneStatus,
 * superAgentOrganizationId, plcOrganizationId, agentId, employerId,
 * employerId).
 *
 * `filters` must be a stable object reference (the caller should build it
 * with useMemo) - the effect depends on the object directly so it only
 * re-runs when a real filter value actually changes, not on every render.
 * Search debouncing is the caller's responsibility, not this hook's.
 *
 * Never fetches when `filters` is falsy (null/undefined) - lets a caller
 * still resolving its own access guard (e.g. an Agent Portal page) pass
 * `null` and get an inert "idle" state instead of an unconditional fetch.
 */
export function useGeofenceZonesList(filters) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!filters) {
      setState({ status: "idle", data: null, error: null });
      return undefined;
    }

    let cancelled = false;
    // Previous data (if any) stays visible while a refetch is in flight -
    // this is what lets the page keep the table/KPI strip mounted and show
    // just an inline loading indicator on a filter change, instead of
    // replacing the whole screen with a spinner every keystroke/click.
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));

    listGeofenceZones(filters)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
